import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  User, 
  Mail, 
  Phone, 
  Calendar, 
  Users, 
  Edit3, 
  Save, 
  X,
  MapPin,
  Settings,
  LogOut
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { collection, getDocs, query, where, orderBy, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import toast from 'react-hot-toast';

const UserProfile = () => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Edit form state
  const [editData, setEditData] = useState({
    name: '',
    email: '',
    phone: '',
    dob: '',
    gender: 'male'
  });

  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      const storedUser = localStorage.getItem('userData');
      if (!storedUser) {
        navigate('/login');
        return;
      }

      const sessionUser = JSON.parse(storedUser);
      
      // Fetch latest user data from Firestore
      const usersQuery = query(
        collection(db, 'users'),
        where('phone', '==', sessionUser.phone)
      );
      const querySnapshot = await getDocs(usersQuery);
      
      if (querySnapshot.empty) {
        toast.error('User not found. Please login again.');
        navigate('/login');
        return;
      }
      
      const userDoc = querySnapshot.docs[0];
      const userData = {
        id: userDoc.id,
        ...userDoc.data()
      };
      
      setUserData(userData);
      setEditData({
        name: userData.name || '',
        email: userData.email || '',
        phone: userData.phone || '',
        dob: userData.dob || '',
        gender: userData.gender || 'male'
      });
      
      // Update localStorage with latest data
      localStorage.setItem('userData', JSON.stringify({
        ...userData,
        isLoggedIn: true,
        loginTime: sessionUser.loginTime || new Date().toISOString()
      }));
      
    } catch (error) {
      console.error('Error loading user data:', error);
      toast.error('Failed to load user data');
      navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      // Update user data in Firestore
      const userRef = doc(db, 'users', userData.id);
      const updateData = {
        name: editData.name,
        email: editData.email,
        phone: editData.phone,
        dob: editData.dob,
        gender: editData.gender,
        updatedAt: new Date()
      };
      
      await updateDoc(userRef, updateData);
      
      // Update local state
      const updatedUser = { ...userData, ...updateData };
      setUserData(updatedUser);
      
      // Update localStorage
      localStorage.setItem('userData', JSON.stringify({
        ...updatedUser,
        isLoggedIn: true,
        loginTime: JSON.parse(localStorage.getItem('userData')).loginTime
      }));
      
      setIsEditing(false);
      toast.success('Profile updated successfully!');
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('userData');
    localStorage.removeItem('customerPhone');
    localStorage.removeItem('userOrders');
    toast.success('Logged out successfully!');
    navigate('/login');
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#f8f9fa',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #e5e7eb',
            borderTop: '4px solid #ff6b35',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }}></div>
          <p style={{ color: '#6b7280' }}>Loading profile...</p>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f8f9fa',
      fontFamily: 'Inter, sans-serif'
    }}>
      {/* Mobile-First Header */}
      <header style={{
        backgroundColor: 'white',
        boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
        padding: '12px 0',
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 16px' }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '12px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <button
                onClick={() => navigate('/order')}
                style={{
                  padding: '8px',
                  backgroundColor: '#f3f4f6',
                  color: '#6b7280',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <ArrowLeft size={18} />
              </button>
              <div style={{
                width: '36px',
                height: '36px',
                background: 'linear-gradient(45deg, #ff6b35, #ff3f3f)',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(255,107,53,0.3)'
              }}>
                <span style={{ color: 'white', fontWeight: '900', fontSize: '1rem' }}>B</span>
              </div>
              <div>
                <h1 style={{ fontSize: '1.125rem', fontWeight: '800', color: '#1f2937', margin: 0 }}>My Profile</h1>
                <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: 0, display: 'none' }}>Manage your account & orders</p>
              </div>
            </div>
            
            <div style={{ 
              display: 'flex', 
              gap: '8px',
              flexWrap: 'wrap'
            }}>
              <Button
                onClick={() => navigate('/addresses')}
                style={{
                  padding: '6px 12px',
                  backgroundColor: '#f3f4f6',
                  color: '#6b7280',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <MapPin size={14} />
                <span style={{ display: 'none' }}>Addresses</span>
              </Button>
              <Button
                onClick={handleLogout}
                style={{
                  padding: '6px 12px',
                  backgroundColor: '#fee2e2',
                  color: '#dc2626',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <LogOut size={14} />
                <span style={{ display: 'none' }}>Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '16px' }}>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
          gap: '20px',
          '@media (max-width: 768px)': {
            gridTemplateColumns: '1fr',
            gap: '16px'
          }
        }}>
          {/* Mobile-First Profile Card */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: '20px',
              boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
              border: '1px solid #e5e7eb',
              height: 'fit-content'
            }}
          >
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <div style={{
                width: '80px',
                height: '80px',
                background: 'linear-gradient(45deg, #ff6b35, #ff3f3f)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 12px',
                boxShadow: '0 6px 20px rgba(255,107,53,0.3)'
              }}>
                <span style={{ color: 'white', fontWeight: '900', fontSize: '2rem' }}>
                  {userData?.name?.charAt(0)?.toUpperCase() || 'U'}
                </span>
              </div>
              <h2 style={{
                fontSize: '20px',
                fontWeight: '700',
                color: '#1f2937',
                margin: '0 0 6px 0'
              }}>
                {userData?.name || 'User'}
              </h2>
              <p style={{
                fontSize: '14px',
                color: '#6b7280',
                margin: 0
              }}>
                Member since {new Date(userData?.registrationTime || Date.now()).toLocaleDateString()}
              </p>
            </div>

            {/* Mobile-First Profile Details */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {isEditing ? (
                <>
                  <div>
                    <Label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', display: 'block' }}>
                      Full Name
                    </Label>
                    <Input
                      type="text"
                      value={editData.name}
                      onChange={(e) => setEditData(prev => ({ ...prev, name: e.target.value }))}
                      style={{
                        padding: '12px',
                        borderRadius: '8px',
                        border: '2px solid #e5e7eb',
                        fontSize: '14px',
                        width: '100%',
                        outline: 'none'
                      }}
                    />
                  </div>

                  <div>
                    <Label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', display: 'block' }}>
                      Email
                    </Label>
                    <Input
                      type="email"
                      value={editData.email}
                      onChange={(e) => setEditData(prev => ({ ...prev, email: e.target.value }))}
                      style={{
                        padding: '12px',
                        borderRadius: '8px',
                        border: '2px solid #e5e7eb',
                        fontSize: '14px',
                        width: '100%',
                        outline: 'none'
                      }}
                    />
                  </div>

                  <div>
                    <Label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', display: 'block' }}>
                      Phone
                    </Label>
                    <Input
                      type="tel"
                      value={editData.phone}
                      onChange={(e) => setEditData(prev => ({ ...prev, phone: e.target.value }))}
                      style={{
                        padding: '12px',
                        borderRadius: '8px',
                        border: '2px solid #e5e7eb',
                        fontSize: '14px',
                        width: '100%',
                        outline: 'none'
                      }}
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div>
                      <Label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', display: 'block' }}>
                        Date of Birth
                      </Label>
                      <Input
                        type="date"
                        value={editData.dob}
                        onChange={(e) => setEditData(prev => ({ ...prev, dob: e.target.value }))}
                        style={{
                          padding: '12px',
                          borderRadius: '8px',
                          border: '2px solid #e5e7eb',
                          fontSize: '14px',
                          width: '100%',
                          outline: 'none'
                        }}
                      />
                    </div>

                    <div>
                      <Label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', display: 'block' }}>
                        Gender
                      </Label>
                      <select
                        value={editData.gender}
                        onChange={(e) => setEditData(prev => ({ ...prev, gender: e.target.value }))}
                        style={{
                          padding: '12px',
                          borderRadius: '8px',
                          border: '2px solid #e5e7eb',
                          fontSize: '14px',
                          width: '100%',
                          outline: 'none',
                          backgroundColor: 'white'
                        }}
                      >
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '12px' }}>
                    <Button
                      onClick={handleSave}
                      style={{
                        flex: 1,
                        padding: '12px',
                        backgroundColor: '#ff6b35',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px'
                      }}
                    >
                      <Save size={16} />
                      Save Changes
                    </Button>
                    <Button
                      onClick={() => setIsEditing(false)}
                      style={{
                        padding: '12px',
                        backgroundColor: '#f3f4f6',
                        color: '#6b7280',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <X size={16} />
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                    <User size={20} color="#6b7280" />
                    <div>
                      <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 2px 0' }}>Full Name</p>
                      <p style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937', margin: 0 }}>{userData?.name || 'Not provided'}</p>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                    <Mail size={20} color="#6b7280" />
                    <div>
                      <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 2px 0' }}>Email</p>
                      <p style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937', margin: 0 }}>{userData?.email || 'Not provided'}</p>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                    <Phone size={20} color="#6b7280" />
                    <div>
                      <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 2px 0' }}>Phone</p>
                      <p style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937', margin: 0 }}>{userData?.phone || 'Not provided'}</p>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                    <Calendar size={20} color="#6b7280" />
                    <div>
                      <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 2px 0' }}>Date of Birth</p>
                      <p style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937', margin: 0 }}>{userData?.dob || 'Not provided'}</p>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                    <Users size={20} color="#6b7280" />
                    <div>
                      <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 2px 0' }}>Gender</p>
                      <p style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937', margin: 0 }}>{userData?.gender || 'Not provided'}</p>
                    </div>
                  </div>

                  <Button
                    onClick={() => setIsEditing(true)}
                    style={{
                      padding: '12px',
                      backgroundColor: '#ff6b35',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px'
                    }}
                  >
                    <Edit3 size={16} />
                    Edit Profile
                  </Button>
                </>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
